import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import {
  CalendarDays, MapPin, ExternalLink, Sparkles, ChevronRight,
  Users, FileText, Image, Video, Play, X, ChevronLeft, ZoomIn,
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';
const STORAGE_BASE = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api').replace(/\/api$/, '');

// Resolve relative /uploads/... paths to full backend URL
const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${STORAGE_BASE}${url}`;
};

// Helper: convert YouTube watch URL → embed URL
const toEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com') && u.searchParams.get('v')) {
      return `https://www.youtube.com/embed/${u.searchParams.get('v')}`;
    }
    if (u.hostname === 'youtu.be') {
      return `https://www.youtube.com/embed${u.pathname}`;
    }
    if (url.includes('/embed/')) return url;
  } catch {}
  return url;
};

// Helper: YouTube thumbnail
const ytThumb = (url) => {
  try {
    const u = new URL(url);
    const vid = u.searchParams.get('v') || (u.hostname === 'youtu.be' ? u.pathname.slice(1) : null);
    if (vid) return `https://img.youtube.com/vi/${vid}/hqdefault.jpg`;
  } catch {}
  return null;
};

/* ─── Video Modal ─────────────────────────────────────────── */
const VideoModal = ({ video, onClose }) => (
  <AnimatePresence>
    {video && (
      <motion.div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/90 p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}>
        <motion.div className="relative w-full max-w-4xl"
          initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.85, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}>
          <button onClick={onClose}
            className="absolute -top-10 right-0 text-white/70 hover:text-white flex items-center gap-1 text-sm">
            <X className="w-5 h-5" /> ปิด
          </button>
          <div className="rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
            <iframe src={toEmbedUrl(video.video_url)} title={video.title}
              className="w-full h-full" allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
          </div>
          {video.title && (
            <p className="text-white text-center mt-4 font-medium" style={{ fontFamily: 'Prompt' }}>{video.title}</p>
          )}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

/* ─── Lightbox ────────────────────────────────────────────── */
const Lightbox = ({ photos, index, onClose }) => {
  const [cur, setCur] = useState(index);
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft') setCur(p => Math.max(0, p - 1));
      if (e.key === 'ArrowRight') setCur(p => Math.min(photos.length - 1, p + 1));
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [photos.length, onClose]);

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/95"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white z-10">
          <X className="w-8 h-8" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); setCur(p => Math.max(0, p - 1)); }}
          className="absolute left-4 text-white/70 hover:text-white z-10 p-2" disabled={cur === 0}>
          <ChevronLeft className="w-8 h-8" />
        </button>
        <motion.img src={resolveImg(photos[cur].photo_url)} alt={photos[cur].caption || ''}
          className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl"
          key={cur} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          onClick={(e) => e.stopPropagation()} />
        <button onClick={(e) => { e.stopPropagation(); setCur(p => Math.min(photos.length - 1, p + 1)); }}
          className="absolute right-4 text-white/70 hover:text-white z-10 p-2" disabled={cur === photos.length - 1}>
          <ChevronRight className="w-8 h-8" />
        </button>
        {photos[cur].caption && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm px-4 py-2 bg-black/50 rounded-full">
            {photos[cur].caption}
          </div>
        )}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-white/40 text-xs">
          {cur + 1} / {photos.length}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ─── Main Page ───────────────────────────────────────────── */
const SymposiumYearPage = () => {
  const { year } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const [lightbox, setLightbox] = useState(null); // index

  useEffect(() => {
    const load = async () => {
      setLoading(true); setNotFound(false);
      try {
        const res = await fetch(`${API_BASE}/symposium-years/${year}`);
        const json = await res.json();
        if (json.success) setData(json.data);
        else setNotFound(true);
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    };
    load();
  }, [year]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (notFound || !data) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="text-6xl mb-4">🔍</div>
        <h2 className="text-2xl font-bold text-gray-700 mb-2" style={{ fontFamily: 'Prompt' }}>ไม่พบข้อมูล</h2>
        <p className="text-gray-500 mb-6" style={{ fontFamily: 'Prompt' }}>ไม่พบข้อมูล Symposium ปี {year}</p>
        <Link to="/" className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition" style={{ fontFamily: 'Prompt' }}>กลับหน้าหลัก</Link>
      </div>
    </div>
  );

  const isCurrent = Number(year) === 2025;
  const highlights = (() => {
    try { return typeof data.highlights === 'string' ? JSON.parse(data.highlights) : data.highlights; } catch { return null; }
  })();
  const videos = data.videos || [];
  const photos = data.photos || [];
  const hasContent = isCurrent || (videos.length > 0 || photos.length > 0);

  const quickLinks = [
    { icon: CalendarDays, label: 'กำหนดการ', to: '/agenda', color: 'from-purple-500 to-purple-700' },
    { icon: Users, label: 'วิทยากร', to: '/speakers', color: 'from-green-500 to-green-700' },
    { icon: FileText, label: 'Proceeding', to: '/proceeding', color: 'from-blue-500 to-blue-700' },
    { icon: FileText, label: 'บทความวิชาการ', to: '/academic-articles', color: 'from-indigo-500 to-indigo-700' },
    { icon: Image, label: 'ภาพในงาน', to: '/images', color: 'from-pink-500 to-pink-700' },
    { icon: Video, label: 'วิดีโอ', to: '/videos', color: 'from-orange-500 to-orange-700' },
  ];

  return (
    <>
      <Helmet>
        <title>{data.title} - SACIT Symposium</title>
        <meta name="description" content={data.subtitle || data.description} />
      </Helmet>

      {/* Modals */}
      <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
      {lightbox !== null && <Lightbox photos={photos} index={lightbox} onClose={() => setLightbox(null)} />}

      <div className="min-h-screen bg-gray-50">

        {/* ── HERO ───────────────────────────────────────────── */}
        <motion.div className="relative w-full overflow-hidden" style={{ minHeight: '420px' }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
          {data.banner_url
            ? <img src={resolveImg(data.banner_url)} alt={data.title} className="absolute inset-0 w-full h-full object-cover" />
            : <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg,#533193 0%,#7C3AED 40%,#1e3a5f 100%)' }} />
          }
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/60" />

          <motion.div className="absolute top-16 right-16"
            animate={{ rotate: 360, scale: [1, 1.3, 1] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}>
            <Sparkles className="w-8 h-8 text-white/30" />
          </motion.div>

          <div className="relative z-10 flex flex-col items-center justify-center text-center px-4 py-28 mt-10">
            <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, delay: 0.2 }}>
              {isCurrent && (
                <span className="inline-block px-4 py-1.5 mb-5 text-xs font-semibold tracking-widest uppercase rounded-full"
                  style={{ background: 'linear-gradient(90deg,#BFB4EE,#B3FFD1)', color: '#533193' }}>ปัจจุบัน</span>
              )}
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4"
                style={{ fontFamily: 'Prompt', textShadow: '0 2px 24px rgba(0,0,0,0.5)' }}>
                {data.title}
              </h1>
              {data.subtitle && (
                <p className="text-xl text-white/85 mb-6 max-w-2xl mx-auto" style={{ fontFamily: 'Prompt' }}>{data.subtitle}</p>
              )}
              <div className="flex flex-wrap items-center justify-center gap-5 text-white/75 text-sm mb-8">
                {data.event_date && <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{data.event_date}</span>}
                {data.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{data.location}</span>}
              </div>
              {data.register_url && isCurrent && (
                <motion.a href={data.register_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold text-[#533193] shadow-lg"
                  style={{ background: 'linear-gradient(90deg,#BFB4EE,#B3FFD1)', fontFamily: 'Prompt' }}
                  whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                  ลงทะเบียนเข้าร่วมงาน <ExternalLink className="w-4 h-4" />
                </motion.a>
              )}
            </motion.div>
          </div>
        </motion.div>

        {/* Sub-nav */}
        <div className="w-full py-3 shadow-md" style={{ background: 'linear-gradient(90deg,#533193 0%,#7C3AED 100%)' }}>
          <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-6 flex-wrap text-sm">
            {[
              { to: '/news', label: 'ข่าวสาร' }, { to: '/exhibition', label: 'นิทรรศการ' },
              { to: '/creative-works', label: 'ผลงานสร้างสรรค์' }, { to: '/proceeding', label: 'Proceeding' },
              { to: '/academic-articles', label: 'บทความวิชาการ' },
            ].map(item => (
              <Link key={item.to} to={item.to} className="text-white/80 hover:text-[#B3FFD1] transition" style={{ fontFamily: 'Prompt' }}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-14 space-y-20">

          {/* ── THEME ───────────────────────────────────────── */}
          {data.theme && data.theme !== 'Coming Soon' && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="rounded-2xl p-8 text-white text-center shadow-xl"
              style={{ background: 'linear-gradient(135deg,#533193,#7C3AED)' }}>
              <p className="text-sm tracking-widest uppercase text-purple-200 mb-3" style={{ fontFamily: 'Prompt' }}>หัวข้อหลักของงาน</p>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Prompt' }}>"{data.theme}"</h2>
            </motion.div>
          )}

          {/* Coming Soon */}
          {(!hasContent && !isCurrent) && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl p-16 text-center shadow-sm border border-gray-200 bg-white">
              <div className="text-6xl mb-4">🗓️</div>
              <h2 className="text-2xl font-bold text-gray-700 mb-3" style={{ fontFamily: 'Prompt' }}>Coming Soon</h2>
              <p className="text-gray-500 max-w-md mx-auto" style={{ fontFamily: 'Prompt' }}>
                รายละเอียดสำหรับ {data.title} จะประกาศเร็วๆ นี้
              </p>
            </motion.div>
          )}

          {/* ── ABOUT ───────────────────────────────────────── */}
          {data.description && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-5" style={{ fontFamily: 'Prompt' }}>เกี่ยวกับงาน</h3>
              <p className="text-gray-600 text-lg leading-relaxed" style={{ fontFamily: 'Prompt', lineHeight: '2.1' }}>
                {data.description}
              </p>
            </motion.div>
          )}

          {/* ── HIGHLIGHTS ──────────────────────────────────── */}
          {Array.isArray(highlights) && highlights.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6" style={{ fontFamily: 'Prompt' }}>ไฮไลท์ของงาน</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {highlights.map((h, i) => (
                  <motion.div key={i} className="flex items-start gap-3 p-5 bg-white rounded-xl shadow-sm border border-gray-100"
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
                    whileHover={{ y: -3, transition: { duration: 0.2 } }}>
                    <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold"
                      style={{ background: 'linear-gradient(135deg,#533193,#B3FFD1)' }}>{i + 1}</div>
                    <p className="text-gray-700 text-sm leading-relaxed" style={{ fontFamily: 'Prompt' }}>{h}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* ── VIDEOS ──────────────────────────────────────── */}
          {videos.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                  style={{ background: 'linear-gradient(135deg,#FF5252,#FF8A80)' }}>
                  <Play className="w-5 h-5 fill-white" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-800" style={{ fontFamily: 'Prompt' }}>วิดีโอภายในงาน</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {videos.map((v, i) => {
                  const thumb = resolveImg(v.thumbnail_url) || ytThumb(v.video_url);
                  return (
                    <motion.div key={v.id}
                      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
                      className="group cursor-pointer" onClick={() => setActiveVideo(v)}>
                      <div className="relative rounded-xl overflow-hidden shadow-md aspect-video bg-gray-900">
                        {thumb
                          ? <img src={thumb} alt={v.title || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                              <Play className="w-12 h-12 text-white/40" />
                            </div>
                        }
                        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <motion.div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg"
                            whileHover={{ scale: 1.1 }}>
                            <Play className="w-6 h-6 text-red-600 fill-red-600 ml-1" />
                          </motion.div>
                        </div>
                      </div>
                      {v.title && (
                        <p className="mt-3 text-sm font-medium text-gray-700 line-clamp-2" style={{ fontFamily: 'Prompt' }}>{v.title}</p>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* ── PHOTOS ──────────────────────────────────────── */}
          {photos.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.25 }}>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                  style={{ background: 'linear-gradient(135deg,#533193,#B3FFD1)' }}>
                  <Image className="w-5 h-5" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-800" style={{ fontFamily: 'Prompt' }}>ภาพบรรยากาศภายในงาน</h3>
              </div>
              <div className="columns-2 md:columns-3 lg:columns-4 gap-3 space-y-3">
                {photos.map((p, i) => (
                  <motion.div key={p.id}
                    initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.04 }}
                    className="group relative break-inside-avoid rounded-xl overflow-hidden cursor-pointer shadow-sm"
                    onClick={() => setLightbox(i)}>
                    <img src={resolveImg(p.photo_url)} alt={p.caption || ''} className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}


        </div>
      </div>
    </>
  );
};

export default SymposiumYearPage;
